<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useThemeVars, type DataTableColumns } from 'naive-ui';
import { searchClause, type ClauseInDb, type ClauseSearchQuery } from '@/api';
import { extractErrorDetail } from '@/utils/errorHandler';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import { contractTypeNames } from '@/components/design/statusMaps';
import { useGaiaToast } from '@/composables/useGaiaToast';

const props = defineProps<{
  show: boolean;
  importedClauseUids?: string[];
}>();

const emit = defineEmits<{
  'update:show': [value: boolean];
  selected: [clause: ClauseInDb];
}>();

const message = useGaiaToast();

const searchText = ref<string>('');
const clauses = ref<ClauseInDb[]>([]);
const loading = ref<boolean>(false);
const totalCount = ref<number>(0);
const currentPage = ref<number>(1);
const pageSize = ref<number>(10);
const selectedUid = ref<string | null>(null);

const importedUidSet = computed<Set<string>>(() => new Set(props.importedClauseUids ?? []));

const columns: DataTableColumns<ClauseInDb> = [
  { title: 'Name', key: 'name', width: 200, ellipsis: { tooltip: true } },
  {
    title: 'Contract Types',
    key: 'contract_types',
    width: 150,
    ellipsis: { tooltip: true },
    render: row => contractTypeNames(row.contract_types).join(', ') || '-',
  },
  {
    title: 'Category',
    key: 'clause_category',
    width: 150,
    render: row => row.clause_category ?? '-',
  },
  { title: 'Jurisdiction', key: 'jurisdiction', width: 120 },
  { title: 'Language', key: 'language', width: 110 },
  { title: 'Content', key: 'content', ellipsis: { tooltip: true } },
];

async function fetchClauses(): Promise<void> {
  loading.value = true;
  try {
    const query: ClauseSearchQuery = {
      text: searchText.value,
      limit: pageSize.value,
      offset: (currentPage.value - 1) * pageSize.value,
      sort_key: 'created_at',
      sort_direction: 'desc',
      similarity_threshold: 0.5,
    };
    const response = await searchClause({ body: query });
    if (!response.data) {
      throw new Error('Failed to search clauses');
    }
    clauses.value = response.data.results;
    totalCount.value = response.data.result_count;
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to search clauses.'));
  } finally {
    loading.value = false;
  }
}

function handleSearch(): void {
  currentPage.value = 1;
  void fetchClauses();
}

function handlePageChange(page: number): void {
  currentPage.value = page;
  void fetchClauses();
}

function handlePageSizeChange(size: number): void {
  pageSize.value = size;
  currentPage.value = 1;
  void fetchClauses();
}

function rowProps(row: ClauseInDb): { style: string; onClick: () => void; title?: string } {
  if (importedUidSet.value.has(row.uid)) {
    return {
      style: 'cursor: not-allowed;',
      title: 'This clause is already in the playbook.',
      onClick: (): void => {},
    };
  }
  return {
    style: 'cursor: pointer;',
    onClick: (): void => {
      selectedUid.value = row.uid;
    },
  };
}

function rowClassName(row: ClauseInDb): string {
  if (importedUidSet.value.has(row.uid)) {
    return 'is-imported';
  }
  return row.uid === selectedUid.value ? 'is-selected' : '';
}

const paginationConfig = {
  get page(): number {
    return currentPage.value;
  },
  get pageSize(): number {
    return pageSize.value;
  },
  get itemCount(): number {
    return totalCount.value;
  },
  showSizePicker: true,
  pageSizes: [10, 20, 50],
  onUpdatePage: handlePageChange,
  onUpdatePageSize: handlePageSizeChange,
};

function handleImport(): void {
  const clause = clauses.value.find(item => item.uid === selectedUid.value);
  if (!clause) {
    message.warning('Select a clause to import.');
    return;
  }
  emit('selected', clause);
  emit('update:show', false);
}

function close(): void {
  emit('update:show', false);
}

const themeVars = useThemeVars();
const pickerVars = computed(() => ({ '--picker-selected': themeVars.value.primaryColor }));

watch(
  () => props.show,
  show => {
    if (show) {
      searchText.value = '';
      currentPage.value = 1;
      selectedUid.value = null;
      void fetchClauses();
    }
  }
);
</script>

<template>
  <n-modal
    :show="show"
    preset="card"
    title="Import clause from library"
    style="width: min(960px, calc(100vw - 32px))"
    :mask-closable="false"
    @update:show="emit('update:show', $event)"
  >
    <n-space vertical :size="16" :style="pickerVars">
      <gaia-search-input
        v-model:value="searchText"
        variant="panel"
        placeholder="Search clauses"
        :style="{ maxWidth: '360px' }"
        @search="handleSearch"
      />

      <n-spin :show="loading">
        <n-data-table
          :columns="columns"
          :data="clauses"
          :bordered="false"
          :scroll-x="900"
          :pagination="paginationConfig"
          :remote="true"
          :row-key="(row: ClauseInDb) => row.uid"
          :row-props="rowProps"
          :row-class-name="rowClassName"
        />
      </n-spin>
    </n-space>

    <template #footer>
      <n-space justify="end">
        <n-button @click="close">Cancel</n-button>
        <n-button type="primary" :disabled="!selectedUid" @click="handleImport">Import</n-button>
      </n-space>
    </template>
  </n-modal>
</template>

<style scoped>
:deep(.n-data-table-tr.is-selected > .n-data-table-td) {
  background-color: color-mix(in srgb, var(--picker-selected) 8%, transparent);
}

:deep(.n-data-table-tr.is-imported > .n-data-table-td) {
  opacity: 0.55;
}
</style>
